import { getRawInputKind } from "@/payroll/ai-script/cell-kind-utils";
import {
  AI_SPREADSHEET_CELL_KIND,
  type AiSpreadsheetCellKind,
  type AiSpreadsheetValueType,
  type IAiSpreadsheetCell,
  type IManualRowCellInput,
  type IManualRowInput,
} from "@/payroll/ai-script/types";

export interface ManualRowSeed {
  col: number;
  value: string;
  kind?: AiSpreadsheetCellKind;
  valueType?: AiSpreadsheetValueType;
}

export const buildManualRowCellInput = (
  rawInput: string,
  kind: AiSpreadsheetCellKind,
  valueType?: AiSpreadsheetValueType,
): IManualRowCellInput => {
  if (kind === AI_SPREADSHEET_CELL_KIND.formula) {
    return {
      kind,
      raw_input: rawInput,
      ...(valueType ? { value_type: valueType } : {}),
    };
  }

  return {
    kind,
    value: rawInput,
    // The API infers the target column type. Keep untyped identifiers as text.
    value_type: valueType ?? "text",
  };
};

export const getSheetContentBounds = (
  cells: Pick<IAiSpreadsheetCell, "row" | "col">[],
) =>
  cells.reduce(
    (bounds, cell) => ({
      maxContentRow: Math.max(bounds.maxContentRow, cell.row),
      maxContentCol: Math.max(bounds.maxContentCol, cell.col),
    }),
    { maxContentRow: 0, maxContentCol: 0 },
  );

export const buildManualRowCells = (
  contentColumnCount: number,
  seed?: ManualRowSeed | ManualRowSeed[],
): Record<string, IManualRowCellInput> => {
  // Accept one seed or several (a drag-fill across the blank row seeds multiple
  // columns of a single new row).
  const seeds = seed ? (Array.isArray(seed) ? seed : [seed]) : [];
  const seedByCol = new Map(seeds.map((item) => [item.col, item]));
  const cells: Record<string, IManualRowCellInput> = {};

  for (let col = 1; col <= Math.max(contentColumnCount, 1); col += 1) {
    const cellSeed = seedByCol.get(col);
    const value = cellSeed?.value ?? "";
    cells[String(col)] = buildManualRowCellInput(
      value,
      cellSeed?.kind ?? getRawInputKind(value),
      cellSeed?.valueType,
    );
  }

  return cells;
};

export const partitionPastedRows = ({
  values,
  startRow,
  startCol,
  maxContentRow,
  cellKinds,
  cellValueTypes,
}: {
  values: readonly (readonly string[])[];
  startRow: number;
  startCol: number;
  maxContentRow: number;
  cellKinds?: readonly (readonly AiSpreadsheetCellKind[])[];
  cellValueTypes?: readonly (readonly (AiSpreadsheetValueType | undefined)[])[];
}) => {
  const existingRows: { gridRow: number; values: readonly string[] }[] = [];
  const trailingRows: { row: IManualRowInput; blank: boolean }[] = [];

  values.forEach((line, lineIndex) => {
    const gridRow = startRow + lineIndex;
    if (gridRow <= maxContentRow) {
      existingRows.push({ gridRow, values: line });
      return;
    }

    const cells = Object.fromEntries(
      line.map((rawValue, colOffset) => {
        const value = String(rawValue ?? "");
        return [
          String(startCol + colOffset),
          buildManualRowCellInput(
            value,
            cellKinds?.[lineIndex]?.[colOffset] ??
              AI_SPREADSHEET_CELL_KIND.value,
            cellValueTypes?.[lineIndex]?.[colOffset],
          ),
        ];
      }),
    );
    trailingRows.push({
      row: { cells },
      blank: line.every((value) => String(value ?? "").trim() === ""),
    });
  });

  let lastNonBlankRow = trailingRows.length;
  while (lastNonBlankRow > 0 && trailingRows[lastNonBlankRow - 1].blank) {
    lastNonBlankRow -= 1;
  }

  return {
    existingRows,
    newRows: trailingRows.slice(0, lastNonBlankRow).map(({ row }) => row),
  };
};
